import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send reset email — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="mx-auto max-w-md px-6 py-16">
      <Card className="rounded-2xl border border-border/60 bg-card p-8 shadow-sm">
        {done ? (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald/10 text-emerald">
              <Mail className="h-5 w-5" />
            </div>
            <h1 className="text-center font-serif text-3xl text-forest">Check your email</h1>
            <p className="mt-2 text-center text-sm text-muted-foreground" data-testid="forgot-password-success">
              If an account exists for <strong className="text-forest">{email}</strong>, we just sent a password reset link.
              It expires in 30 minutes.
            </p>
            <p className="mt-4 text-center text-xs text-muted-foreground">
              Didn't get it? Check your spam folder, or <button onClick={() => setDone(false)} className="font-medium text-primary hover:underline" data-testid="forgot-password-try-again">try a different email</button>.
            </p>
            <Link to="/login" className="mt-6 block text-center text-sm font-medium text-primary hover:underline" data-testid="forgot-back-to-login">
              <ArrowLeft className="mr-1 inline h-3.5 w-3.5" /> Back to sign in
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-serif text-3xl text-forest">Forgot your password?</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              No problem — enter your email and we'll send you a link to choose a new one.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Email</label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-email-input"
                  className="mt-1 h-11"
                />
              </div>
              <Button type="submit" disabled={submitting} data-testid="forgot-submit-btn" className="w-full rounded-full bg-primary py-6 text-primary-foreground">
                {submitting ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="mt-5 block text-center text-sm font-medium text-primary hover:underline" data-testid="forgot-back-to-login">
              <ArrowLeft className="mr-1 inline h-3.5 w-3.5" /> Back to sign in
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
